/**
 * Redux Slice for Global Event Notification System
 * 
 * Manages state for aggregated notifications, real-time updates,
 * and interaction with the global notification center.
 */

import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { API_BASE_URL as RAW_API_BASE_URL } from '../services/api';
import {
  GlobalEventNotification,
  GlobalNotificationSummary,
  GlobalNotificationFilters,
  NotificationGrouping,
  NotificationDisplayOptions,
  ConversationAggregation,
  NotificationWebSocketEvent
} from '../types/globalNotification';

interface GlobalNotificationState {
  // Core notification data
  notifications: GlobalEventNotification[];
  conversationAggregations: ConversationAggregation[];
  summary: GlobalNotificationSummary | null;
  
  // UI state
  loading: boolean;
  error: string | null;
  isGlobalCenterOpen: boolean;
  selectedNotificationIds: string[];
  
  // Filtering and display
  filters: GlobalNotificationFilters;
  displayOptions: NotificationDisplayOptions;
  grouping: NotificationGrouping | null;
  
  // Real-time state
  websocketConnected: boolean;
  lastUpdate: string | null;
  
  // Interaction state
  expandedConversations: string[];
  readStatusPending: string[]; // Notifications pending read status update
}

const initialDisplayOptions: NotificationDisplayOptions = {
  view_mode: 'grouped',
  sort_by: 'date',
  sort_order: 'desc',
  show_previews: true,
  show_participant_avatars: true,
  group_by: 'conversation'
};

const initialState: GlobalNotificationState = {
  notifications: [],
  conversationAggregations: [],
  summary: null,
  loading: false,
  error: null,
  isGlobalCenterOpen: false,
  selectedNotificationIds: [],
  filters: {},
  displayOptions: initialDisplayOptions,
  grouping: null,
  websocketConnected: false,
  lastUpdate: null,
  expandedConversations: [],
  readStatusPending: []
};

// API Base URL
const API_BASE_URL = RAW_API_BASE_URL.replace(/\/$/, '');

// Async thunks for API interactions
export const fetchGlobalNotifications = createAsyncThunk(
  'globalNotifications/fetchNotifications',
  async (params?: { filters?: GlobalNotificationFilters; page?: number; limit?: number }) => {
    // Build query parameters
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.set('page', params.page.toString());
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    
    // Add filter parameters
    if (params?.filters) {
      const filters = params.filters;
      if (filters.event_type?.length) {
        queryParams.set('event_type', filters.event_type.join(','));
      }
      if (filters.priority_level?.length) {
        queryParams.set('priority_level', filters.priority_level.join(','));
      }
      if (filters.unread !== undefined) {
        queryParams.set('unread', filters.unread.toString());
      }
      if (filters.archived !== undefined) {
        queryParams.set('archived', filters.archived.toString());
      }
      if (filters.conversation_id) {
        queryParams.set('conversation_id', filters.conversation_id);
      }
      if (filters.search_query) {
        queryParams.set('search', filters.search_query);
      }
    }

    // Use fetch with credentials for cookie-based authentication
    const response = await fetch(`${API_BASE_URL}/global/?${queryParams.toString()}`, {
      method: 'GET',
      credentials: 'include', // Include httpOnly cookies
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication token not found. Please login again.');
      }
      
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to fetch global notifications (${response.status})`);
    }
    
    return response.json();
  }
);

export const fetchNotificationSummary = createAsyncThunk(
  'globalNotifications/fetchSummary',
  async () => {
    const response = await fetch(`${API_BASE_URL}/global/summary/`, {
      method: 'GET',
      credentials: 'include', // Include httpOnly cookies
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication token not found. Please login again.');
      }
      
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to fetch notification summary');
    }
    
    return response.json();
  }
);

export const markNotificationAsRead = createAsyncThunk(
  'globalNotifications/markAsRead',
  async ({ notificationId, conversationId }: { notificationId?: string; conversationId?: string }) => {
    const body: any = {};
    if (notificationId) body.notification_id = notificationId;
    if (conversationId) body.conversation_id = conversationId;

    const response = await fetch(`${API_BASE_URL}/global/mark_read/`, {
      method: 'POST',
      credentials: 'include', // Include httpOnly cookies
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body)
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to mark notification as read');
    }
    
    return { notificationId, conversationId };
  }
);

export const archiveNotifications = createAsyncThunk(
  'globalNotifications/archive',
  async ({ notificationIds, conversationId }: { notificationIds?: string[]; conversationId?: string }) => {
    const body: any = {};
    if (notificationIds) body.notification_ids = notificationIds;
    if (conversationId) body.conversation_id = conversationId;

    const response = await fetch(`${API_BASE_URL}/global/archive/`, {
      method: 'POST',
      credentials: 'include', // Include httpOnly cookies
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body)
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to archive notifications');
    }
    
    return { notificationIds, conversationId };
  }
);

export const navigateToConversation = createAsyncThunk(
  'globalNotifications/navigateToConversation',
  async ({ conversationId, notificationId }: { conversationId: string; notificationId?: string }) => {
    // This thunk handles navigation and marking as read
    return { conversationId, notificationId };
  }
);

// Redux slice
const globalNotificationSlice = createSlice({
  name: 'globalNotifications',
  initialState,
  reducers: {
    // WebSocket event handlers
    handleWebSocketEvent: (state, action: PayloadAction<NotificationWebSocketEvent>) => {
      const event = action.payload;
      
      switch (event.type) {
        case 'aggregated_notification':
          if (event.notification) {
            const existingIndex = state.notifications.findIndex(n => n.id === event.notification!.id);
            if (existingIndex >= 0) {
              state.notifications[existingIndex] = event.notification;
            } else {
              state.notifications.unshift(event.notification);
            }
            state.lastUpdate = new Date().toISOString();
          }
          break;
          
        case 'notification_read_update':
          if (event.conversation_id) {
            state.notifications.forEach(n => {
              if (n.conversation_id === event.conversation_id) {
                n.is_read = true;
                n.read_at = event.read_at || new Date().toISOString();
              }
            });
            // Remove from pending list
            state.readStatusPending = state.readStatusPending.filter(id => 
              !state.notifications.some(n => n.id === id && n.conversation_id === event.conversation_id)
            );
          }
          break;
          
        case 'notification_deleted':
          if (event.notification_id) {
            state.notifications = state.notifications.filter(n => n.id !== event.notification_id);
          }
          break;
      }
    },
    
    // UI state management
    openGlobalNotificationCenter: (state) => {
      state.isGlobalCenterOpen = true;
    },
    
    closeGlobalNotificationCenter: (state) => {
      state.isGlobalCenterOpen = false;
      state.selectedNotificationIds = [];
    },
    
    toggleGlobalNotificationCenter: (state) => {
      state.isGlobalCenterOpen = !state.isGlobalCenterOpen;
      if (!state.isGlobalCenterOpen) {
        state.selectedNotificationIds = [];
      }
    },
    
    // Filtering and display
    setGlobalFilters: (state, action: PayloadAction<GlobalNotificationFilters>) => {
      state.filters = action.payload;
    },
    
    updateGlobalFilter: (state, action: PayloadAction<{ key: keyof GlobalNotificationFilters; value: any }>) => {
      (state.filters as any)[action.payload.key] = action.payload.value;
    },
    
    clearGlobalFilters: (state) => {
      state.filters = {};
    },
    
    setDisplayOptions: (state, action: PayloadAction<Partial<NotificationDisplayOptions>>) => {
      state.displayOptions = { ...state.displayOptions, ...action.payload };
    },
    
    // Selection management
    selectNotification: (state, action: PayloadAction<string>) => {
      if (!state.selectedNotificationIds.includes(action.payload)) {
        state.selectedNotificationIds.push(action.payload);
      }
    },
    
    deselectNotification: (state, action: PayloadAction<string>) => {
      state.selectedNotificationIds = state.selectedNotificationIds.filter(id => id !== action.payload);
    },
    
    selectAllNotifications: (state) => {
      state.selectedNotificationIds = state.notifications.map(n => n.id);
    },
    
    clearSelection: (state) => {
      state.selectedNotificationIds = [];
    },
    
    // Conversation management
    toggleConversationExpansion: (state, action: PayloadAction<string>) => {
      const conversationId = action.payload;
      const index = state.expandedConversations.indexOf(conversationId);
      
      if (index >= 0) {
        state.expandedConversations.splice(index, 1);
      } else {
        state.expandedConversations.push(conversationId);
      }
    },
    
    expandConversation: (state, action: PayloadAction<string>) => {
      if (!state.expandedConversations.includes(action.payload)) {
        state.expandedConversations.push(action.payload);
      }
    },
    
    collapseConversation: (state, action: PayloadAction<string>) => {
      state.expandedConversations = state.expandedConversations.filter(id => id !== action.payload);
    },
    
    collapseAllConversations: (state) => {
      state.expandedConversations = [];
    },
    
    // WebSocket connection state
    setWebSocketConnectionState: (state, action: PayloadAction<boolean>) => {
      state.websocketConnected = action.payload;
    },
    
    // Error handling
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    
    clearError: (state) => {
      state.error = null;
    },
    
    // Optimistic updates
    addPendingReadStatus: (state, action: PayloadAction<string>) => {
      if (!state.readStatusPending.includes(action.payload)) {
        state.readStatusPending.push(action.payload);
      }
    },
    
    removePendingReadStatus: (state, action: PayloadAction<string>) => {
      state.readStatusPending = state.readStatusPending.filter(id => id !== action.payload);
    },
    
    // Direct notification updates
    updateNotificationInState: (state, action: PayloadAction<GlobalEventNotification>) => {
      const index = state.notifications.findIndex(n => n.id === action.payload.id);
      if (index >= 0) {
        state.notifications[index] = action.payload;
      }
    },
    
    removeNotificationFromState: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload);
      state.selectedNotificationIds = state.selectedNotificationIds.filter(id => id !== action.payload);
    }
  },
  
  extraReducers: (builder) => {
    // Fetch notifications
    builder
      .addCase(fetchGlobalNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGlobalNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = action.payload.results || action.payload;
        state.lastUpdate = new Date().toISOString();
      })
      .addCase(fetchGlobalNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch global notifications';
      });
    
    // Fetch summary
    builder
      .addCase(fetchNotificationSummary.fulfilled, (state, action) => {
        state.summary = action.payload;
      })
      .addCase(fetchNotificationSummary.rejected, (state, action) => {
        if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
          console.error('Failed to fetch notification summary:', action.error.message);
        }
      });
    
    // Mark as read
    builder
      .addCase(markNotificationAsRead.pending, (state, action) => {
        // Add to pending list for optimistic UI updates
        if (action.meta.arg.notificationId) {
          state.readStatusPending.push(action.meta.arg.notificationId);
        }
      })
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const { notificationId, conversationId } = action.payload;
        
        // Update notifications based on ID or conversation
        state.notifications.forEach(notification => {
          if ((notificationId && notification.id === notificationId) ||
              (conversationId && notification.conversation_id === conversationId)) {
            notification.is_read = true;
            notification.read_at = new Date().toISOString();
          }
        });
        
        // Remove from pending list
        if (notificationId) {
          state.readStatusPending = state.readStatusPending.filter(id => id !== notificationId);
        }
      })
      .addCase(markNotificationAsRead.rejected, (state, action) => {
        // Remove from pending list on failure
        const { notificationId } = action.meta.arg;
        if (notificationId) {
          state.readStatusPending = state.readStatusPending.filter(id => id !== notificationId);
        }
        state.error = action.error.message || 'Failed to mark notification as read';
      });
    
    // Archive notifications
    builder
      .addCase(archiveNotifications.fulfilled, (state, action) => {
        const { notificationIds, conversationId } = action.payload;
        
        state.notifications.forEach(notification => {
          if ((notificationIds && notificationIds.includes(notification.id)) ||
              (conversationId && notification.conversation_id === conversationId)) {
            notification.is_archived = true;
            notification.archived_at = new Date().toISOString();
          }
        });
        
        // Clear selection
        state.selectedNotificationIds = [];
      })
      .addCase(archiveNotifications.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to archive notifications';
      });
    
    // Navigate to conversation
    builder.addCase(navigateToConversation.fulfilled, (state, action) => {
      const { conversationId, notificationId } = action.payload;
      
      // Mark associated notifications as read (optimistically)
      state.notifications.forEach(notification => {
        if (notification.conversation_id === conversationId) {
          notification.is_read = true;
          notification.read_at = new Date().toISOString();
        }
      });
      
      // Close notification center
      state.isGlobalCenterOpen = false;
    });
  }
});

export const {
  handleWebSocketEvent,
  openGlobalNotificationCenter,
  closeGlobalNotificationCenter,
  toggleGlobalNotificationCenter,
  setGlobalFilters,
  updateGlobalFilter,
  clearGlobalFilters,
  setDisplayOptions,
  selectNotification,
  deselectNotification,
  selectAllNotifications,
  clearSelection,
  toggleConversationExpansion,
  expandConversation,
  collapseConversation,
  collapseAllConversations,
  setWebSocketConnectionState,
  setError,
  clearError,
  addPendingReadStatus,
  removePendingReadStatus,
  updateNotificationInState,
  removeNotificationFromState
} = globalNotificationSlice.actions;

export default globalNotificationSlice.reducer;